import { useEffect, useState } from "react";
import axios from "axios";
import { format, addMonths, subMonths, isSameDay, parseISO, endOfWeek, isWithinInterval, startOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { Loader2, Trash2, Plus, LogOut, Lock, Star, CalendarDays, MessageSquareQuote, BookOpen, Upload, Eye, EyeOff, Inbox, Mail, Clock, ChevronLeft, ChevronRight, Copy, ExternalLink, NotebookPen, X, FileText, Download, FileCheck2, Wand2, CheckCircle2, Repeat, PoundSterling, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ADMIN, ADMIN_RESOURCE } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("dates");

  // Blocked dates
  const [dates, setDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [reason, setReason] = useState("");
  const [addingDate, setAddingDate] = useState(false);

  // Blocked time slots
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotDate, setSlotDate] = useState("");
  const [slotTime, setSlotTime] = useState("");
  const [slotReason, setSlotReason] = useState("");
  const [addingSlot, setAddingSlot] = useState(false);

  const AVAILABLE_TIME_OPTIONS = [
    "09:00", "10:00", "11:00", "12:00", "13:00",
    "14:00", "15:00", "16:00", "16:30",
    "17:00", "17:30", "18:00", "18:30",
    "19:00", "19:30", "20:00",
  ];

  // Testimonials
  const [testimonials, setTestimonials] = useState([]);
  const [loadingT, setLoadingT] = useState(false);
  const [tName, setTName] = useState("");
  const [tQuote, setTQuote] = useState("");
  const [tRating, setTRating] = useState("5");
  const [addingT, setAddingT] = useState(false);

  // Resources
  const [resources, setResources] = useState([]);
  const [loadingR, setLoadingR] = useState(false);
  const [rTitle, setRTitle] = useState("");
  const [rDesc, setRDesc] = useState("");
  const [rCategory, setRCategory] = useState("chemistry");
  const [rLevel, setRLevel] = useState("KS4");
  const [rType, setRType] = useState("checklist");
  const [rPrice, setRPrice] = useState("4.99");
  const [rFile, setRFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Contact messages
  const [messages, setMessages] = useState([]);
  const [loadingM, setLoadingM] = useState(false);

  // Diary (admin-only bookings calendar)
  const [bookings, setBookings] = useState([]);
  const [loadingB, setLoadingB] = useState(false);
  const [diaryMonth, setDiaryMonth] = useState(new Date());
  const [diarySelectedDate, setDiarySelectedDate] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // booking obj being rescheduled
  const [reDate, setReDate] = useState("");
  const [reTime, setReTime] = useState("");
  const [rescheduling, setRescheduling] = useState(false);
  const [notesDraft, setNotesDraft] = useState({}); // {bookingId: text} local drafts

  // Manual booking (admin adds a lesson booked outside the website)
  const [manualOpen, setManualOpen] = useState(false);
  const [manualStudent, setManualStudent] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [manualSubject, setManualSubject] = useState("Chemistry");
  const [manualKeyStage, setManualKeyStage] = useState("KS4");
  const [manualDate, setManualDate] = useState("");
  const [manualTime, setManualTime] = useState("");
  const [manualAmount, setManualAmount] = useState("");
  const [manualNotes, setManualNotes] = useState("");
  const [savingManual, setSavingManual] = useState(false);
  // Recurring options
  const [manualRepeat, setManualRepeat] = useState(false);
  const [manualRepeatMode, setManualRepeatMode] = useState("count"); // count | until
  const [manualRepeatCount, setManualRepeatCount] = useState(8);
  const [manualRepeatUntil, setManualRepeatUntil] = useState("");

  // Invoices / Receipts (admin PDF generator)
  const [invDocType, setInvDocType] = useState("invoice"); // invoice | receipt
  const [invNumber, setInvNumber] = useState("");
  const [invIssueDate, setInvIssueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [invDueDate, setInvDueDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().slice(0, 10);
  });
  const [invPaidDate, setInvPaidDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [invPaymentMethod, setInvPaymentMethod] = useState("Bank transfer");
  const [invBillName, setInvBillName] = useState("");
  const [invBillEmail, setInvBillEmail] = useState("");
  const [invBillAddress, setInvBillAddress] = useState("");
  const [invItems, setInvItems] = useState([
    { description: "", quantity: 1, unit_price: 30 },
  ]);
  const [invAccountName, setInvAccountName] = useState(() => localStorage.getItem("kt_bank_account_name") || "");
  const [invSortCode, setInvSortCode] = useState(() => localStorage.getItem("kt_bank_sort_code") || "");
  const [invAccountNumber, setInvAccountNumber] = useState(() => localStorage.getItem("kt_bank_account_number") || "");
  const [invReference, setInvReference] = useState("");
  const [invNotes, setInvNotes] = useState("");
  const [invSelectedBookingId, setInvSelectedBookingId] = useState("");
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const loadBookings = async (pw) => {
    setLoadingB(true);
    try {
      const b = await axios.get(`${API}/admin/bookings`, { params: { password: pw } });
      setBookings(b.data?.bookings || []);
    } catch {
      // ignore; toast already shown by loadAll if relevant
    } finally {
      setLoadingB(false);
    }
  };

  const loadAll = async (pw) => {
    setLoadingDates(true);
    setLoadingSlots(true);
    setLoadingT(true);
    setLoadingR(true);
    setLoadingM(true);
    try {
      const [d, s, t, r, m, b] = await Promise.all([
        axios.get(`${API}/admin/blocked-dates`, { params: { password: pw } }),
        axios.get(`${API}/admin/blocked-slots`, { params: { password: pw } }),
        axios.get(`${API}/admin/testimonials`, { params: { password: pw } }),
        axios.get(`${API}/admin/resources`, { params: { password: pw } }),
        axios.get(`${API}/admin/messages`, { params: { password: pw } }),
        axios.get(`${API}/admin/bookings`, { params: { password: pw } }),
      ]);
      setDates(d.data?.dates || []);
      setSlots(s.data?.slots || []);
      setTestimonials(t.data?.testimonials || []);
      setResources(r.data?.resources || []);
      setMessages(m.data?.messages || []);
      setBookings(b.data?.bookings || []);
    } catch {
      toast.error("Could not load admin data.");
    } finally {
      setLoadingDates(false);
      setLoadingSlots(false);
      setLoadingT(false);
      setLoadingR(false);
      setLoadingM(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      setAuthed(true);
      await loadAll(password);
    } catch {
      toast.error("Wrong password.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleAddDate = async (e) => {
    e.preventDefault();
    if (!newDate) {
      toast.error("Pick a date to block.");
      return;
    }
    setAddingDate(true);
    try {
      await axios.post(`${API}/admin/blocked-dates`, {
        password,
        date: newDate,
        reason: reason || undefined,
      });
      toast.success(`Blocked ${newDate}`);
      setNewDate("");
      setReason("");
      const d = await axios.get(`${API}/admin/blocked-dates`, { params: { password } });
      setDates(d.data?.dates || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not block date.";
      toast.error(msg);
    } finally {
      setAddingDate(false);
    }
  };

  const handleRemoveDate = async (date) => {
    try {
      await axios.delete(`${API}/admin/blocked-dates/${date}`, {
        params: { password },
      });
      toast.success(`Unblocked ${date}`);
      const d = await axios.get(`${API}/admin/blocked-dates`, { params: { password } });
      setDates(d.data?.dates || []);
    } catch {
      toast.error("Could not remove date.");
    }
  };

  const handleAddSlot = async (e) => {
    e.preventDefault();
    if (!slotDate || !slotTime) {
      toast.error("Pick a date and a time slot.");
      return;
    }
    setAddingSlot(true);
    try {
      await axios.post(`${API}/admin/blocked-slots`, {
        password,
        date: slotDate,
        time: slotTime,
        reason: slotReason || undefined,
      });
      toast.success(`Blocked ${slotDate} at ${slotTime}`);
      setSlotDate("");
      setSlotTime("");
      setSlotReason("");
      const s = await axios.get(`${API}/admin/blocked-slots`, { params: { password } });
      setSlots(s.data?.slots || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not block slot.";
      toast.error(msg);
    } finally {
      setAddingSlot(false);
    }
  };

  const handleRemoveSlot = async (id) => {
    try {
      await axios.delete(`${API}/admin/blocked-slots/${id}`, {
        params: { password },
      });
      toast.success("Slot unblocked");
      const s = await axios.get(`${API}/admin/blocked-slots`, { params: { password } });
      setSlots(s.data?.slots || []);
    } catch {
      toast.error("Could not remove slot.");
    }
  };

  const handleAddTestimonial = async (e) => {
    e.preventDefault();
    if (!tName.trim() || !tQuote.trim()) {
      toast.error("Name and quote are required.");
      return;
    }
    setAddingT(true);
    try {
      await axios.post(`${API}/admin/testimonials`, {
        password,
        name: tName.trim(),
        quote: tQuote.trim(),
        rating: parseInt(tRating, 10) || 5,
      });
      toast.success("Review added.");
      setTName("");
      setTQuote("");
      setTRating("5");
      const t = await axios.get(`${API}/admin/testimonials`, { params: { password } });
      setTestimonials(t.data?.testimonials || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not add review.";
      toast.error(msg);
    } finally {
      setAddingT(false);
    }
  };

  const handleRemoveTestimonial = async (id) => {
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { params: { password } });
      toast.success("Review removed.");
      const t = await axios.get(`${API}/admin/testimonials`, { params: { password } });
      setTestimonials(t.data?.testimonials || []);
    } catch {
      toast.error("Could not remove review.");
    }
  };

  const handleDeleteMessage = async (id) => {
    try {
      await axios.delete(`${API}/admin/messages/${id}`, { params: { password } });
      toast.success("Message deleted.");
      const m = await axios.get(`${API}/admin/messages`, { params: { password } });
      setMessages(m.data?.messages || []);
    } catch {
      toast.error("Could not delete message.");
    }
  };

  // ---------- Diary handlers ----------
  const saveBookingNotes = async (bookingId, notes) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}`, {
        password,
        notes,
      });
      toast.success("Notes saved.");
      await loadBookings(password);
    } catch {
      toast.error("Could not save notes.");
    }
  };

  const openReschedule = (booking) => {
    setRescheduleTarget(booking);
    setReDate(booking.session_date);
    setReTime(booking.session_time);
  };

  const submitReschedule = async () => {
    if (!rescheduleTarget) return;
    setRescheduling(true);
    try {
      await axios.patch(`${API}/admin/bookings/${rescheduleTarget.id}`, {
        password,
        session_date: reDate,
        session_time: reTime,
      });
      toast.success(`Moved to ${format(parseISO(reDate), "EEE d MMM")} · ${reTime}`);
      setRescheduleTarget(null);
      await loadBookings(password);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reschedule.");
    } finally {
      setRescheduling(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm("Cancel this booking? It will disappear from your diary.")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, { params: { password } });
      toast.success("Booking cancelled.");
      await loadBookings(password);
    } catch {
      toast.error("Could not cancel booking.");
    }
  };

  const openManualDialog = (prefillDate) => {
    setManualStudent("");
    setManualEmail("");
    setManualSubject("Chemistry");
    setManualKeyStage("KS4");
    setManualDate(prefillDate || format(new Date(), "yyyy-MM-dd"));
    setManualTime("");
    setManualAmount("");
    setManualNotes("");
    setManualRepeat(false);
    setManualRepeatMode("count");
    setManualRepeatCount(8);
    setManualRepeatUntil("");
    setManualOpen(true);
  };

  const submitManualBooking = async () => {
    if (!manualStudent.trim()) {
      toast.error("Student name is required.");
      return;
    }
    if (!manualDate || !manualTime) {
      toast.error("Date and time are required.");
      return;
    }
    if (manualKeyStage === "KS5" && manualSubject !== "Chemistry") {
      toast.error("KS5 A-Level is Chemistry only.");
      return;
    }
    if (manualRepeat) {
      if (manualRepeatMode === "count" && (!manualRepeatCount || manualRepeatCount < 2)) {
        toast.error("Number of sessions must be at least 2.");
        return;
      }
      if (manualRepeatMode === "until" && (!manualRepeatUntil || manualRepeatUntil <= manualDate)) {
        toast.error("End date must be after the first session.");
        return;
      }
    }
    setSavingManual(true);
    try {
      const payload = {
        password,
        student_name: manualStudent.trim(),
        subject: manualSubject,
        key_stage: manualKeyStage,
        session_date: manualDate,
        session_time: manualTime,
      };
      if (manualEmail.trim()) payload.email = manualEmail.trim();
      if (manualNotes.trim()) payload.notes = manualNotes.trim();
      const amt = parseFloat(manualAmount);
      if (!isNaN(amt) && amt >= 0) payload.amount = amt;

      if (manualRepeat) {
        payload.repeat_mode = manualRepeatMode;
        if (manualRepeatMode === "count") payload.repeat_count = Number(manualRepeatCount);
        else payload.repeat_until = manualRepeatUntil;
        const res = await axios.post(`${API}/admin/bookings/manual/recurring`, payload);
        const created = res.data?.created || [];
        const stopped = res.data?.stopped_at;
        if (stopped && created.length === 0) {
          toast.error(`Couldn't start the series: ${stopped.reason} on ${format(parseISO(stopped.date), "EEE d MMM")}.`);
        } else if (stopped) {
          toast.warning(
            `Added ${created.length} lesson${created.length === 1 ? "" : "s"}. Stopped at ${format(parseISO(stopped.date), "EEE d MMM")} — ${stopped.reason}. Adjust and resume.`,
            { duration: 8000 }
          );
          setManualOpen(false);
          setDiarySelectedDate(created[created.length - 1].date);
        } else {
          toast.success(`Added ${created.length} weekly lessons for ${manualStudent.trim()}.`);
          setManualOpen(false);
          setDiarySelectedDate(manualDate);
        }
      } else {
        await axios.post(`${API}/admin/bookings`, payload);
        toast.success(`Lesson added for ${manualStudent.trim()} on ${format(parseISO(manualDate), "EEE d MMM")} · ${manualTime}`);
        setManualOpen(false);
        setDiarySelectedDate(manualDate);
      }
      await loadBookings(password);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add lesson.");
    } finally {
      setSavingManual(false);
    }
  };

  const toggleBookingCompleted = async (bookingId, currentlyCompleted) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}/complete`, {
        password,
        completed: !currentlyCompleted,
      });
      toast.success(currentlyCompleted ? "Marked as not completed." : "Lesson marked as completed.");
      await loadBookings(password);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update lesson.");
    }
  };

  const icsFeedUrl = password
    ? `${API}/admin/diary.ics?token=${encodeURIComponent(password)}`
    : "";
  const icsWebcalUrl = icsFeedUrl
    ? icsFeedUrl.replace(/^https?:\/\//, "webcal://")
    : "";

  const copyIcsUrl = async () => {
    try {
      await navigator.clipboard.writeText(icsFeedUrl);
      toast.success("Calendar feed URL copied!");
    } catch {
      toast.error("Copy failed — long-press to copy manually.");
    }
  };

  const refreshResources = async () => {
    const r = await axios.get(`${API}/admin/resources`, { params: { password } });
    setResources(r.data?.resources || []);
  };

  const handleAddResource = async (e) => {
    e.preventDefault();
    if (!rTitle.trim() || !rFile) {
      toast.error("Title and file are required.");
      return;
    }
    const price = parseFloat(rPrice);
    if (isNaN(price) || price < 0) {
      toast.error("Enter a valid price.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("password", password);
      form.append("title", rTitle.trim());
      form.append("description", rDesc.trim());
      form.append("category", rCategory);
      form.append("level", rLevel);
      form.append("resource_type", rType);
      form.append("price", String(price));
      form.append("file", rFile);
      await axios.post(`${API}/admin/resources`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Resource published.");
      setRTitle("");
      setRDesc("");
      setRPrice("4.99");
      setRFile(null);
      // reset file input visually
      const input = document.querySelector('[data-testid="admin-resource-file-input"]');
      if (input) input.value = "";
      await refreshResources();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not upload resource.";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleToggleResource = async (id) => {
    try {
      await axios.patch(`${API}/admin/resources/${id}/toggle`, null, {
        params: { password },
      });
      await refreshResources();
    } catch {
      toast.error("Could not toggle resource.");
    }
  };

  const handleDeleteResource = async (id) => {
    if (!window.confirm("Delete this resource and its file? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/resources/${id}`, { params: { password } });
      toast.success("Resource deleted.");
      await refreshResources();
    } catch {
      toast.error("Could not delete resource.");
    }
  };

  const handleLogout = () => {
    setAuthed(false);
    setPassword("");
    setDates([]);
    setTestimonials([]);
    setResources([]);
  };

  // ---------- Invoice / Receipt helpers ----------
  const invSubtotal = invItems.reduce(
    (sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0),
    0
  );
  const invIsReceipt = invDocType === "receipt";

  const handleAddInvItem = () => {
    setInvItems((prev) => [...prev, { description: "", quantity: 1, unit_price: 30 }]);
  };
  const handleRemoveInvItem = (idx) => {
    setInvItems((prev) => prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx));
  };
  const handleInvItemChange = (idx, field, value) => {
    setInvItems((prev) => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const loadFromBooking = (bookingId) => {
    setInvSelectedBookingId(bookingId);
    if (!bookingId) return;
    const b = bookings.find((x) => x.id === bookingId);
    if (!b) return;
    setInvBillName(b.student_name || "");
    setInvBillEmail(b.email || "");
    const ks = b.key_stage || "KS4";
    const unitPrice = ks === "KS5" ? 40 : 30;
    const dateStr = b.session_date || b.date;
    const timeStr = b.session_time || b.time || "";
    let prettyDate = dateStr;
    try { prettyDate = format(parseISO(dateStr), "EEE d MMM yyyy"); } catch {}
    const desc = `${ks} ${b.subject || "Tuition"} · 60-min session · ${prettyDate} ${timeStr}`.trim();
    setInvItems([{ description: desc, quantity: 1, unit_price: Number(b.amount) || unitPrice }]);
    if (b.status === "paid" || b.payment_status === "paid") {
      setInvDocType("receipt");
      setInvPaymentMethod("Card (Stripe)");
    }
    setInvReference(b.id?.slice(0, 8) || "");
  };

  const handleGenerateInvoicePdf = async () => {
    if (!invBillName.trim()) {
      toast.error("Please enter a 'Bill to' name.");
      return;
    }
    if (!invItems.length || invItems.every((it) => !it.description?.trim())) {
      toast.error("Please add at least one line item with a description.");
      return;
    }
    // Persist bank details locally for next time
    localStorage.setItem("kt_bank_account_name", invAccountName || "");
    localStorage.setItem("kt_bank_sort_code", invSortCode || "");
    localStorage.setItem("kt_bank_account_number", invAccountNumber || "");

    setGeneratingPdf(true);
    try {
      const payload = {
        password,
        doc_type: invDocType,
        doc_number: invNumber || null,
        issue_date: invIssueDate || null,
        due_date: invIsReceipt ? null : (invDueDate || null),
        paid_date: invIsReceipt ? (invPaidDate || null) : null,
        payment_method: invIsReceipt ? (invPaymentMethod || null) : null,
        bill_to_name: invBillName,
        bill_to_email: invBillEmail || null,
        bill_to_address: invBillAddress || null,
        line_items: invItems
          .filter((it) => (it.description || "").trim())
          .map((it) => ({
            description: it.description,
            quantity: Number(it.quantity) || 1,
            unit_price: Number(it.unit_price) || 0,
          })),
        bank_details: invIsReceipt ? null : {
          account_name: invAccountName || null,
          sort_code: invSortCode || null,
          account_number: invAccountNumber || null,
          reference: invReference || null,
        },
        notes: invNotes || null,
      };
      const res = await axios.post(`${API}/admin/invoice/pdf`, payload, {
        responseType: "blob",
      });
      const docNum = res.headers["x-document-number"] || (invIsReceipt ? "receipt" : "invoice");
      const safeName = (invBillName || "client").replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 40);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${docNum}_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${invIsReceipt ? "Receipt" : "Invoice"} downloaded.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate PDF.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const todayIso = format(new Date(), "yyyy-MM-dd");

  return (
    <div className="min-h-screen bg-[#fbfcfe]">
      <Navbar />

      <section className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
            Admin
          </div>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2a3b5c]">
            Manage your site
          </h1>
          <p className="mt-4 text-[#5c6b85]">
            Block out holidays or days off, and add reviews from past students
            and parents.
          </p>

          {!authed && (
            <form
              onSubmit={handleLogin}
              className="mt-12 max-w-md rounded-3xl bg-[#f0f6fc] border border-[#dce6f0] p-8"
            >
              <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium flex items-center gap-2">
                <Lock className="w-3.5 h-3.5" /> Admin password
              </Label>
              <Input
                data-testid={ADMIN.passwordInput}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
              />
              <Button
                data-testid={ADMIN.loginButton}
                type="submit"
                disabled={loggingIn || !password}
                className="mt-5 w-full rounded-full py-6 bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c]"
              >
                {loggingIn ? "Checking..." : "Sign in"}
              </Button>
              <p className="mt-4 text-xs text-[#5c6b85]">
                Default password: <code className="bg-white px-1 py-0.5 rounded">missbossa</code>{" "}
                (change via <code>ADMIN_PASSWORD</code> in the backend env).
              </p>
            </form>
          )}

          {authed && (
            <div className="mt-10 space-y-10">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="inline-flex rounded-full bg-[#f0f6fc] border border-[#dce6f0] p-1 flex-wrap">
                  <button
                    onClick={() => setTab("diary")}
                    data-testid="admin-tab-diary"
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm transition-colors ${
                      tab === "diary"
                        ? "bg-[#8ab8e8] text-[#2a3b5c]"
                        : "text-[#5c6b85] hover:text-[#3d6fa8]"
                    }`}
                  >
                    <NotebookPen className="w-4 h-4" /> Diary
                    {bookings.length > 0 && (
                      <span className="ml-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#ffe89a] text-[#2a3b5c]">
                        {bookings.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setTab("dates")}
                    data-testid="admin-tab-dates"
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm transition-colors ${
                      tab === "dates"
                        ? "bg-[#8ab8e8] text-[#2a3b5c]"
                        : "text-[#5c6b85] hover:text-[#3d6fa8]"
                    }`}
                  >
                    <CalendarDays className="w-4 h-4" /> Availability
                  </button>
                  <button
                    onClick={() => setTab("testimonials")}
                    data-testid="admin-tab-testimonials"
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm transition-colors ${
                      tab === "testimonials"
                        ? "bg-[#8ab8e8] text-[#2a3b5c]"
                        : "text-[#5c6b85] hover:text-[#3d6fa8]"
                    }`}
                  >
                    <MessageSquareQuote className="w-4 h-4" /> Reviews
                  </button>
                  <button
                    onClick={() => setTab("resources")}
                    data-testid="admin-tab-resources"
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm transition-colors ${
                      tab === "resources"
                        ? "bg-[#8ab8e8] text-[#2a3b5c]"
                        : "text-[#5c6b85] hover:text-[#3d6fa8]"
                    }`}
                  >
                    <BookOpen className="w-4 h-4" /> Resources
                  </button>
                  <button
                    onClick={() => setTab("messages")}
                    data-testid="admin-tab-messages"
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm transition-colors ${
                      tab === "messages"
                        ? "bg-[#8ab8e8] text-[#2a3b5c]"
                        : "text-[#5c6b85] hover:text-[#3d6fa8]"
                    }`}
                  >
                    <Inbox className="w-4 h-4" /> Messages
                    {messages.length > 0 && (
                      <span className="ml-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#ffe89a] text-[#2a3b5c]">
                        {messages.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setTab("invoicing")}
                    data-testid="admin-tab-invoicing"
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm transition-colors ${
                      tab === "invoicing"
                        ? "bg-[#8ab8e8] text-[#2a3b5c]"
                        : "text-[#5c6b85] hover:text-[#3d6fa8]"
                    }`}
                  >
                    <FileText className="w-4 h-4" /> Invoicing
                  </button>
                </div>
                <button
                  data-testid={ADMIN.logoutButton}
                  onClick={handleLogout}
                  className="inline-flex items-center gap-2 text-sm text-[#5c6b85] hover:text-[#3d6fa8]"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              </div>

              {tab === "diary" && (
                <div className="space-y-6">
                  {/* Diary header with manual add */}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="font-heading text-2xl font-semibold text-[#2a3b5c]">
                        Diary
                      </h2>
                      <p className="mt-1 text-sm text-[#5c6b85]">
                        Your week at a glance, plus every booking — online and offline.
                      </p>
                    </div>
                    <Button
                      data-testid="diary-add-lesson-button"
                      onClick={() => openManualDialog(diarySelectedDate)}
                      className="rounded-full bg-[#2a3b5c] hover:bg-[#1f2c45] text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add lesson manually
                    </Button>
                  </div>

                  {/* Upcoming this week summary */}
                  {(() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
                    const weekStart = startOfWeek(today, { weekStartsOn: 1 });
                    const monthStart = startOfMonth(today);
                    const monthEnd = endOfMonth(today);
                    const upcoming = bookings
                      .filter((b) => {
                        try {
                          const d = parseISO(b.session_date);
                          return isWithinInterval(d, { start: today, end: weekEnd });
                        } catch {
                          return false;
                        }
                      })
                      .sort((a, b) => {
                        if (a.session_date !== b.session_date)
                          return a.session_date.localeCompare(b.session_date);
                        return (a.session_time || "").localeCompare(b.session_time || "");
                      });
                    // Earnings — completed lessons only
                    const inRange = (iso, start, end) => {
                      try {
                        const d = parseISO(iso);
                        return isWithinInterval(d, { start, end });
                      } catch { return false; }
                    };
                    const completedWeek = bookings.filter(
                      (b) => b.completed && inRange(b.session_date, weekStart, weekEnd)
                    );
                    const completedMonth = bookings.filter(
                      (b) => b.completed && inRange(b.session_date, monthStart, monthEnd)
                    );
                    const sumAmount = (arr) =>
                      arr.reduce((s, b) => s + (Number(b.amount) || 0), 0);
                    const weekEarnings = sumAmount(completedWeek);
                    const monthEarnings = sumAmount(completedMonth);
                    const weekLabel = `${format(today, "d MMM")} – ${format(weekEnd, "d MMM")}`;
                    return (
                      <>
                      <div
                        data-testid="diary-upcoming-week-card"
                        className="rounded-3xl bg-gradient-to-br from-[#eaf3fc] to-[#fff8df] border border-[#dce6f0] p-6 md:p-7"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full bg-white border border-[#dce6f0] flex items-center justify-center shrink-0">
                              <CalendarDays className="w-4 h-4 text-[#3d6fa8]" />
                            </div>
                            <div>
                              <h3 className="font-heading font-semibold text-[#2a3b5c]">
                                Upcoming lessons this week
                              </h3>
                              <p className="mt-1 text-xs uppercase tracking-[0.15em] text-[#5c6b85]">
                                {weekLabel}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <div
                              data-testid="diary-week-earned-pill"
                              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#dce6f0] px-3 py-1 text-sm text-[#2a3b5c]"
                              title="Sum of completed lessons this week"
                            >
                              <PoundSterling className="w-3.5 h-3.5 text-[#3d6fa8]" />
                              <span className="font-semibold">£{weekEarnings.toFixed(2)}</span>
                              <span className="text-[#5c6b85]">earned</span>
                            </div>
                            <div
                              data-testid="diary-upcoming-count"
                              className="rounded-full bg-white border border-[#dce6f0] px-3 py-1 text-sm font-semibold text-[#2a3b5c]"
                            >
                              {upcoming.length} lesson{upcoming.length === 1 ? "" : "s"}
                            </div>
                          </div>
                        </div>

                        {loadingB ? (
                          <p className="mt-4 text-sm text-[#5c6b85]">Loading…</p>
                        ) : upcoming.length === 0 ? (
                          <p
                            data-testid="diary-upcoming-empty"
                            className="mt-4 text-sm text-[#5c6b85]"
                          >
                            Nothing booked between now and Sunday — enjoy the breather.
                          </p>
                        ) : (
                          <ul className="mt-4 space-y-2">
                            {upcoming.slice(0, 6).map((b) => (
                              <li
                                key={b.id}
                                data-testid={`diary-upcoming-${b.id}`}
                                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white border border-[#dce6f0] px-4 py-2.5"
                              >
                                <button
                                  onClick={() => setDiarySelectedDate(b.session_date)}
                                  className="flex items-center gap-3 min-w-0 text-left"
                                >
                                  <div className="shrink-0 w-12 text-center">
                                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#3d6fa8] font-medium">
                                      {format(parseISO(b.session_date), "EEE")}
                                    </div>
                                    <div className="font-heading text-lg font-semibold text-[#2a3b5c] leading-tight">
                                      {format(parseISO(b.session_date), "d")}
                                    </div>
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold text-[#2a3b5c] truncate">
                                      <Clock className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-[#3d6fa8]" />
                                      {b.session_time} · {b.student_name}
                                    </div>
                                    <div className="text-xs text-[#5c6b85] truncate">
                                      {b.subject} ({b.key_stage})
                                    </div>
                                  </div>
                                </button>
                              </li>
                            ))}
                            {upcoming.length > 6 && (
                              <li className="text-xs text-[#5c6b85] px-2">
                                + {upcoming.length - 6} more this week
                              </li>
                            )}
                          </ul>
                        )}
                      </div>

                      {/* Earnings mini-stats row */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
                        <div
                          data-testid="stats-week-earnings"
                          className="rounded-3xl bg-white border border-[#dce6f0] p-5 flex items-center gap-4"
                        >
                          <div className="w-11 h-11 rounded-2xl bg-[#eaf3fc] border border-[#dce6f0] flex items-center justify-center shrink-0">
                            <PoundSterling className="w-5 h-5 text-[#3d6fa8]" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5c6b85] font-medium">
                              This week · earned
                            </div>
                            <div className="mt-1 font-heading text-2xl font-semibold text-[#2a3b5c] leading-none">
                              £{weekEarnings.toFixed(2)}
                            </div>
                            <div className="text-xs text-[#5c6b85] mt-1">
                              {completedWeek.length} completed lesson{completedWeek.length === 1 ? "" : "s"}
                            </div>
                          </div>
                        </div>
                        <div
                          data-testid="stats-month-earnings"
                          className="rounded-3xl bg-white border border-[#dce6f0] p-5 flex items-center gap-4"
                        >
                          <div className="w-11 h-11 rounded-2xl bg-[#fff8df] border border-[#f0e3a8] flex items-center justify-center shrink-0">
                            <TrendingUp className="w-5 h-5 text-[#a48414]" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5c6b85] font-medium">
                              {format(today, "MMMM")} · earned
                            </div>
                            <div className="mt-1 font-heading text-2xl font-semibold text-[#2a3b5c] leading-none">
                              £{monthEarnings.toFixed(2)}
                            </div>
                            <div className="text-xs text-[#5c6b85] mt-1">
                              {completedMonth.length} completed lesson{completedMonth.length === 1 ? "" : "s"}
                            </div>
                          </div>
                        </div>
                        <div
                          data-testid="stats-week-completed"
                          className="rounded-3xl bg-white border border-[#dce6f0] p-5 flex items-center gap-4"
                        >
                          <div className="w-11 h-11 rounded-2xl bg-[#eaf3fc] border border-[#dce6f0] flex items-center justify-center shrink-0">
                            <CheckCircle2 className="w-5 h-5 text-[#3d6fa8]" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5c6b85] font-medium">
                              Completed this week
                            </div>
                            <div className="mt-1 font-heading text-2xl font-semibold text-[#2a3b5c] leading-none">
                              {completedWeek.length}
                            </div>
                            <div className="text-xs text-[#5c6b85] mt-1">
                              Sessions marked done
                            </div>
                          </div>
                        </div>
                      </div>
                      </>
                    );
                  })()}

                  {/* iCal subscription card */}
                  <div className="rounded-3xl bg-[#fff8df] border border-[#f0e3a8] p-6 md:p-7">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-white border border-[#f0e3a8] flex items-center justify-center shrink-0">
                        <ExternalLink className="w-4 h-4 text-[#3d6fa8]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-semibold text-[#2a3b5c]">
                          Sync this diary to your Outlook calendar
                        </h3>
                        <p className="mt-1 text-sm text-[#5c6b85]">
                          Copy the private link below, then in Outlook go to
                          <strong> Calendar → Add calendar → Subscribe from web</strong>,
                          paste, and name it "Tutoring". Outlook refreshes it every hour or so.
                          Keep this link private — anyone with it can see your bookings.
                        </p>
                        <div className="mt-4 flex flex-col sm:flex-row gap-2">
                          <input
                            data-testid="admin-ics-feed-url"
                            readOnly
                            value={icsFeedUrl}
                            onFocus={(e) => e.target.select()}
                            className="flex-1 rounded-full border border-[#f0e3a8] bg-white px-4 py-2.5 text-xs text-[#2a3b5c] font-mono truncate"
                          />
                          <Button
                            data-testid="admin-ics-copy-button"
                            onClick={copyIcsUrl}
                            className="rounded-full bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c]"
                          >
                            <Copy className="w-3.5 h-3.5 mr-2" />
                            Copy link
                          </Button>
                          <a
                            data-testid="admin-ics-webcal-button"
                            href={icsWebcalUrl}
                            className="inline-flex items-center justify-center rounded-full bg-white border border-[#f0e3a8] text-[#2a3b5c] px-4 py-2.5 text-sm hover:bg-[#fff3c8] transition-colors"
                          >
                            <ExternalLink className="w-3.5 h-3.5 mr-2" />
                            Open in calendar app
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Calendar + day view */}
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    {/* Month calendar */}
                    <div className="lg:col-span-2 rounded-3xl bg-white border border-[#dce6f0] p-6">
                      <div className="flex items-center justify-between">
                        <button
                          data-testid="diary-prev-month"
                          onClick={() => setDiaryMonth(subMonths(diaryMonth, 1))}
                          className="p-2 rounded-full hover:bg-[#f0f6fc] text-[#3d6fa8]"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <h3 className="font-heading font-semibold text-[#2a3b5c]">
                          {format(diaryMonth, "MMMM yyyy")}
                        </h3>
                        <button
                          data-testid="diary-next-month"
                          onClick={() => setDiaryMonth(addMonths(diaryMonth, 1))}
                          className="p-2 rounded-full hover:bg-[#f0f6fc] text-[#3d6fa8]"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Day-of-week header */}
                      <div className="mt-4 grid grid-cols-7 text-center text-[10px] uppercase tracking-[0.15em] text-[#5c6b85]">
                        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                          <div key={d} className="py-2">{d}</div>
                        ))}
                      </div>

                      {/* Month grid */}
                      <div className="grid grid-cols-7 gap-1">
                        {(() => {
                          const year = diaryMonth.getFullYear();
                          const month = diaryMonth.getMonth();
                          const firstOfMonth = new Date(year, month, 1);
                          // shift so Monday = 0
                          const startOffset = (firstOfMonth.getDay() + 6) % 7;
                          const daysInMonth = new Date(year, month + 1, 0).getDate();
                          const cells = [];
                          for (let i = 0; i < startOffset; i++) {
                            cells.push(<div key={`pad-${i}`} className="aspect-square" />);
                          }
                          for (let day = 1; day <= daysInMonth; day++) {
                            const cellDate = new Date(year, month, day);
                            const iso = format(cellDate, "yyyy-MM-dd");
                            const dayBookings = bookings.filter((b) => b.session_date === iso);
                            const isSelected =
                              diarySelectedDate && isSameDay(parseISO(diarySelectedDate), cellDate);
                            const isToday = isSameDay(cellDate, new Date());
                            cells.push(
                              <button
                                key={iso}
                                data-testid={`diary-day-${iso}`}
                                onClick={() => setDiarySelectedDate(iso)}
                                className={`aspect-square rounded-xl flex flex-col items-center justify-center gap-0.5 text-sm transition-colors relative ${
                                  isSelected
                                    ? "bg-[#8ab8e8] text-[#2a3b5c] font-semibold"
                                    : isToday
                                    ? "bg-[#fff8df] text-[#2a3b5c] hover:bg-[#fff3c8]"
                                    : "hover:bg-[#f0f6fc] text-[#2a3b5c]"
                                }`}
                              >
                                <span>{day}</span>
                                {dayBookings.length > 0 && (
                                  <span
                                    className={`text-[10px] font-medium px-1.5 py-0 rounded-full ${
                                      isSelected
                                        ? "bg-[#2a3b5c] text-white"
                                        : "bg-[#3d6fa8] text-white"
                                    }`}
                                  >
                                    {dayBookings.length}
                                  </span>
                                )}
                              </button>
                            );
                          }
                          return cells;
                        })()}
                      </div>

                      <div className="mt-5 flex items-center gap-4 text-xs text-[#5c6b85]">
                        <span className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-[#fff8df] border border-[#f0e3a8]" />
                          Today
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-[#3d6fa8]" />
                          Booking
                        </span>
                      </div>
                    </div>

                    {/* Day detail */}
                    <div className="lg:col-span-3 rounded-3xl bg-white border border-[#dce6f0] p-6 min-h-[400px]">
                      {!diarySelectedDate && (
                        <div className="h-full flex flex-col items-center justify-center text-center py-16">
                          <NotebookPen className="w-8 h-8 text-[#8ab8e8] mb-3" />
                          <p className="text-sm text-[#5c6b85]">
                            Pick a day on the calendar to see your bookings,
                            add prep notes, reschedule or cancel.
                          </p>
                        </div>
                      )}
                      {diarySelectedDate && (() => {
                        const dayBookings = bookings.filter(
                          (b) => b.session_date === diarySelectedDate
                        );
                        return (
                          <div>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <h3 className="font-heading text-xl font-semibold text-[#2a3b5c]">
                                  {format(parseISO(diarySelectedDate), "EEEE, d MMMM yyyy")}
                                </h3>
                                <p className="mt-1 text-sm text-[#5c6b85]">
                                  {dayBookings.length === 0
                                    ? "No bookings on this day."
                                    : `${dayBookings.length} booking${dayBookings.length === 1 ? "" : "s"}`}
                                </p>
                              </div>
                              <button
                                data-testid="diary-add-to-day-button"
                                onClick={() => openManualDialog(diarySelectedDate)}
                                className="inline-flex items-center gap-1.5 rounded-full bg-[#fff8df] border border-[#f0e3a8] px-3 py-1.5 text-xs font-medium text-[#2a3b5c] hover:bg-[#fff3c8] transition-colors"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Add lesson on this day
                              </button>
                            </div>
                            <div className="mt-5 space-y-4">
                              {dayBookings.map((b) => {
                                const draftKey = b.id;
                                const draftVal = draftKey in notesDraft ? notesDraft[draftKey] : (b.notes || "");
                                const bookingIsPast = (() => {
                                  try {
                                    return parseISO(b.session_date) < new Date(new Date().toDateString());
                                  } catch { return false; }
                                })();
                                const suggestComplete = bookingIsPast && !b.completed && b.status !== "cancelled";
                                return (
                                  <div
                                    key={b.id}
                                    data-testid={`diary-booking-${b.id}`}
                                    className={`rounded-2xl border p-5 ${
                                      b.completed
                                        ? "border-[#c9e6c9] bg-[#f2faf1]"
                                        : "border-[#dce6f0] bg-[#fbfcfe]"
                                    }`}
                                  >
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                      <div className="min-w-0">
                                        <div className="font-heading text-lg font-semibold text-[#2a3b5c] flex items-center gap-2 flex-wrap">
                                          {b.session_time} · {b.student_name}
                                          {b.completed && (
                                            <span
                                              data-testid={`diary-booking-completed-badge-${b.id}`}
                                              className="inline-flex items-center gap-1 rounded-full bg-[#e6f3e2] border border-[#c9e6c9] px-2.5 py-0.5 text-[11px] font-medium text-[#3f7a2d]"
                                            >
                                              <CheckCircle2 className="w-3 h-3" /> Completed
                                            </span>
                                          )}
                                        </div>
                                        <div className="mt-1 text-sm text-[#5c6b85]">
                                          {b.subject} ({b.key_stage}) ·{" "}
                                          <a
                                            href={`mailto:${b.email}`}
                                            className="text-[#3d6fa8] hover:underline"
                                          >
                                            {b.email}
                                          </a>
                                        </div>
                                        <div className="mt-1 text-xs text-[#5c6b85]">
                                          Paid £{Number(b.amount || 0).toFixed(2)}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {b.completed ? (
                                          <Button
                                            data-testid={`diary-uncomplete-${b.id}`}
                                            onClick={() => toggleBookingCompleted(b.id, true)}
                                            variant="outline"
                                            className="rounded-full text-sm border-[#dce6f0] text-[#5c6b85]"
                                          >
                                            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                            Undo complete
                                          </Button>
                                        ) : (
                                          <Button
                                            data-testid={`diary-complete-${b.id}`}
                                            onClick={() => toggleBookingCompleted(b.id, false)}
                                            variant="outline"
                                            className="rounded-full text-sm border-[#c9e6c9] text-[#3f7a2d] hover:bg-[#f2faf1]"
                                          >
                                            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                            Mark completed
                                          </Button>
                                        )}
                                        <Button
                                          data-testid={`diary-reschedule-${b.id}`}
                                          onClick={() => openReschedule(b)}
                                          variant="outline"
                                          className="rounded-full text-sm border-[#dce6f0]"
                                        >
                                          <CalendarDays className="w-3.5 h-3.5 mr-1.5" />
                                          Reschedule
                                        </Button>
                                        <button
                                          data-testid={`diary-cancel-${b.id}`}
                                          onClick={() => cancelBooking(b.id)}
                                          className="p-2 rounded-full text-[#5c6b85] hover:text-red-600 hover:bg-red-50 transition-colors"
                                          title="Cancel booking"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                    {suggestComplete && (
                                      <div
                                        data-testid={`diary-complete-suggest-${b.id}`}
                                        className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-[#fff8df] border border-[#f0e3a8] px-3 py-2"
                                      >
                                        <span className="text-xs text-[#a48414]">
                                          This lesson has passed — did it go ahead?
                                        </span>
                                        <button
                                          data-testid={`diary-complete-suggest-confirm-${b.id}`}
                                          onClick={() => toggleBookingCompleted(b.id, false)}
                                          className="inline-flex items-center gap-1 rounded-full bg-[#2a3b5c] hover:bg-[#1f2c45] text-white text-xs font-medium px-3 py-1"
                                        >
                                          <CheckCircle2 className="w-3.5 h-3.5" />
                                          Yes, mark completed
                                        </button>
                                      </div>
                                    )}
                                    <div className="mt-4">
                                      <Label className="text-[11px] uppercase tracking-[0.18em] text-[#3d6fa8] font-medium">
                                        Lesson notes
                                      </Label>
                                      <Textarea
                                        data-testid={`diary-notes-${b.id}`}
                                        value={draftVal}
                                        placeholder="e.g. Tom wants to go through electrolysis, do past paper Q3"
                                        onChange={(e) =>
                                          setNotesDraft({ ...notesDraft, [draftKey]: e.target.value })
                                        }
                                        onBlur={() => {
                                          if (draftVal !== (b.notes || "")) {
                                            saveBookingNotes(b.id, draftVal);
                                          }
                                        }}
                                        rows={3}
                                        className="mt-2 rounded-2xl bg-white border-[#dce6f0] resize-none text-sm"
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Reschedule dialog */}
                  <Dialog
                    open={!!rescheduleTarget}
                    onOpenChange={(open) => !open && setRescheduleTarget(null)}
                  >
                    <DialogContent data-testid="diary-reschedule-dialog" className="bg-white rounded-3xl">
                      <DialogHeader>
                        <DialogTitle className="font-heading text-[#2a3b5c]">
                          Reschedule lesson
                        </DialogTitle>
                      </DialogHeader>
                      {rescheduleTarget && (
                        <div className="space-y-4">
                          <p className="text-sm text-[#5c6b85]">
                            Moving <strong>{rescheduleTarget.student_name}</strong>'s{" "}
                            {rescheduleTarget.subject} lesson. Currently{" "}
                            {(() => {
                              try {
                                return format(parseISO(rescheduleTarget.session_date), "EEE d MMM");
                              } catch {
                                return rescheduleTarget.session_date;
                              }
                            })()}{" "}
                            at {rescheduleTarget.session_time}.
                          </p>
                          <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                              New date
                            </Label>
                            <Input
                              data-testid="diary-reschedule-date"
                              type="date"
                              value={reDate}
                              onChange={(e) => setReDate(e.target.value)}
                              className="mt-2 rounded-2xl h-12 border-[#dce6f0]"
                            />
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                              New time
                            </Label>
                            <Select value={reTime} onValueChange={setReTime}>
                              <SelectTrigger
                                data-testid="diary-reschedule-time"
                                className="mt-2 rounded-2xl h-12 border-[#dce6f0]"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {AVAILABLE_TIME_OPTIONS.map((t) => (
                                  <SelectItem key={t} value={t}>
                                    {t}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <p className="text-xs text-[#5c6b85]">
                            The site won't notify the parent — please message them directly to confirm the new time.
                          </p>
                        </div>
                      )}
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => setRescheduleTarget(null)}
                          className="rounded-full"
                        >
                          Cancel
                        </Button>
                        <Button
                          data-testid="diary-reschedule-submit"
                          disabled={rescheduling}
                          onClick={submitReschedule}
                          className="rounded-full bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c]"
                        >
                          {rescheduling ? "Saving..." : "Save change"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  {/* Manual add-lesson dialog */}
                  <Dialog open={manualOpen} onOpenChange={setManualOpen}>
                    <DialogContent
                      data-testid="diary-manual-dialog"
                      className="bg-white rounded-3xl max-w-md max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden"
                    >
                      <DialogHeader className="px-6 pt-6 pb-2">
                        <DialogTitle className="font-heading text-[#2a3b5c]">
                          Add a lesson manually
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1 min-h-0">
                        <p className="text-sm text-[#5c6b85]">
                          For lessons booked outside the website (cash, regulars, returning students).
                          This goes straight into your diary and Outlook feed — no payment required.
                        </p>
                        <div>
                          <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                            Student name
                          </Label>
                          <Input
                            data-testid="manual-student-name"
                            value={manualStudent}
                            onChange={(e) => setManualStudent(e.target.value)}
                            placeholder="e.g. Aisha Khan"
                            className="mt-2 rounded-2xl h-12 border-[#dce6f0]"
                          />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                            Parent email <span className="lowercase tracking-normal text-[#9aa6bd]">(optional)</span>
                          </Label>
                          <Input
                            data-testid="manual-email"
                            type="email"
                            value={manualEmail}
                            onChange={(e) => setManualEmail(e.target.value)}
                            placeholder="parent@example.com"
                            className="mt-2 rounded-2xl h-12 border-[#dce6f0]"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                              Key stage
                            </Label>
                            <Select
                              value={manualKeyStage}
                              onValueChange={(v) => {
                                setManualKeyStage(v);
                                if (v === "KS5") setManualSubject("Chemistry");
                              }}
                            >
                              <SelectTrigger
                                data-testid="manual-key-stage"
                                className="mt-2 rounded-2xl h-12 border-[#dce6f0]"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="KS3">KS3</SelectItem>
                                <SelectItem value="KS4">KS4 (GCSE)</SelectItem>
                                <SelectItem value="KS5">KS5 (A-Level)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                              Subject
                            </Label>
                            <Select value={manualSubject} onValueChange={setManualSubject}>
                              <SelectTrigger
                                data-testid="manual-subject"
                                className="mt-2 rounded-2xl h-12 border-[#dce6f0]"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {manualKeyStage === "KS5" ? (
                                  <SelectItem value="Chemistry">Chemistry</SelectItem>
                                ) : (
                                  <>
                                    <SelectItem value="Chemistry">Chemistry</SelectItem>
                                    <SelectItem value="Physics">Physics</SelectItem>
                                    <SelectItem value="Biology">Biology</SelectItem>
                                    <SelectItem value="Maths">Maths</SelectItem>
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                              Date
                            </Label>
                            <Input
                              data-testid="manual-date"
                              type="date"
                              value={manualDate}
                              onChange={(e) => setManualDate(e.target.value)}
                              className="mt-2 rounded-2xl h-12 border-[#dce6f0]"
                            />
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                              Time
                            </Label>
                            <Select value={manualTime} onValueChange={setManualTime}>
                              <SelectTrigger
                                data-testid="manual-time"
                                className="mt-2 rounded-2xl h-12 border-[#dce6f0]"
                              >
                                <SelectValue placeholder="Pick a time" />
                              </SelectTrigger>
                              <SelectContent>
                                {AVAILABLE_TIME_OPTIONS.map((t) => (
                                  <SelectItem key={t} value={t}>
                                    {t}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                            Amount paid <span className="lowercase tracking-normal text-[#9aa6bd]">(optional — defaults to your tier price)</span>
                          </Label>
                          <Input
                            data-testid="manual-amount"
                            type="number"
                            step="0.01"
                            min="0"
                            value={manualAmount}
                            onChange={(e) => setManualAmount(e.target.value)}
                            placeholder={manualKeyStage === "KS5" ? "50.00" : "40.00"}
                            className="mt-2 rounded-2xl h-12 border-[#dce6f0]"
                          />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                            Notes <span className="lowercase tracking-normal text-[#9aa6bd]">(optional)</span>
                          </Label>
                          <Textarea
                            data-testid="manual-notes"
                            value={manualNotes}
                            onChange={(e) => setManualNotes(e.target.value)}
                            placeholder="e.g. Paid in cash. Mock exam revision."
                            className="mt-2 rounded-2xl border-[#dce6f0] min-h-[80px]"
                          />
                        </div>

                        {/* Recurring options */}
                        <div className="rounded-2xl border border-[#dce6f0] bg-[#f0f6fc] p-4">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              data-testid="manual-repeat-toggle"
                              type="checkbox"
                              checked={manualRepeat}
                              onChange={(e) => setManualRepeat(e.target.checked)}
                              className="mt-1 h-4 w-4 rounded border-[#dce6f0] accent-[#3d6fa8]"
                            />
                            <span className="flex-1">
                              <span className="font-heading text-sm font-semibold text-[#2a3b5c] flex items-center gap-1.5">
                                <Repeat className="w-3.5 h-3.5" /> Repeat weekly
                              </span>
                              <span className="block text-xs text-[#5c6b85] mt-0.5">
                                Book the same time every week (great for regulars).
                              </span>
                            </span>
                          </label>
                          {manualRepeat && (
                            <div className="mt-4 space-y-3">
                              <div className="inline-flex w-full rounded-2xl bg-white border border-[#dce6f0] p-1">
                                <button
                                  type="button"
                                  data-testid="manual-repeat-mode-count"
                                  onClick={() => setManualRepeatMode("count")}
                                  className={`flex-1 rounded-xl px-3 py-1.5 text-xs transition-colors ${
                                    manualRepeatMode === "count"
                                      ? "bg-[#8ab8e8] text-[#2a3b5c] font-medium"
                                      : "text-[#5c6b85] hover:text-[#3d6fa8]"
                                  }`}
                                >
                                  For N sessions
                                </button>
                                <button
                                  type="button"
                                  data-testid="manual-repeat-mode-until"
                                  onClick={() => setManualRepeatMode("until")}
                                  className={`flex-1 rounded-xl px-3 py-1.5 text-xs transition-colors ${
                                    manualRepeatMode === "until"
                                      ? "bg-[#8ab8e8] text-[#2a3b5c] font-medium"
                                      : "text-[#5c6b85] hover:text-[#3d6fa8]"
                                  }`}
                                >
                                  Until end date
                                </button>
                              </div>
                              {manualRepeatMode === "count" ? (
                                <div>
                                  <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                                    Number of sessions (including today)
                                  </Label>
                                  <Input
                                    data-testid="manual-repeat-count"
                                    type="number"
                                    min="2"
                                    max="52"
                                    value={manualRepeatCount}
                                    onChange={(e) => setManualRepeatCount(e.target.value)}
                                    className="mt-2 rounded-2xl h-12 border-[#dce6f0] bg-white"
                                  />
                                </div>
                              ) : (
                                <div>
                                  <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                                    Repeat weekly until
                                  </Label>
                                  <Input
                                    data-testid="manual-repeat-until"
                                    type="date"
                                    value={manualRepeatUntil}
                                    min={manualDate}
                                    onChange={(e) => setManualRepeatUntil(e.target.value)}
                                    className="mt-2 rounded-2xl h-12 border-[#dce6f0] bg-white"
                                  />
                                </div>
                              )}
                              <p className="text-[11px] text-[#5c6b85] leading-relaxed">
                                We'll stop at the first clash (blocked date, blocked slot, or existing booking)
                                and tell you which week failed so you can fix it and resume.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <DialogFooter className="px-6 py-4 border-t border-[#eef2f8] bg-white shrink-0">
                        <Button
                          variant="outline"
                          onClick={() => setManualOpen(false)}
                          className="rounded-full"
                        >
                          Cancel
                        </Button>
                        <Button
                          data-testid="manual-submit"
                          disabled={savingManual}
                          onClick={submitManualBooking}
                          className="rounded-full bg-[#2a3b5c] hover:bg-[#1f2c45] text-white"
                        >
                          {savingManual ? "Adding…" : (manualRepeat ? "Add series" : "Add lesson")}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              )}

              {tab === "dates" && (
                <>
                  <form
                    onSubmit={handleAddDate}
                    className="rounded-3xl bg-[#f0f6fc] border border-[#dce6f0] p-8 grid grid-cols-1 md:grid-cols-3 gap-5"
                  >
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Date to block
                      </Label>
                      <Input
                        data-testid={ADMIN.dateInput}
                        type="date"
                        min={todayIso}
                        value={newDate}
                        onChange={(e) => setNewDate(e.target.value)}
                        className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Reason (optional)
                      </Label>
                      <Input
                        data-testid={ADMIN.reasonInput}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Easter holiday, family day"
                        className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                      />
                    </div>
                    <div className="md:col-span-3 flex justify-end">
                      <Button
                        data-testid={ADMIN.addButton}
                        type="submit"
                        disabled={addingDate || !newDate}
                        className="rounded-full px-6 py-6 bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c]"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {addingDate ? "Adding..." : "Block this date"}
                      </Button>
                    </div>
                  </form>

                  <div className="rounded-3xl bg-white border border-[#dce6f0] p-2">
                    <div className="px-6 py-4 border-b border-[#dce6f0] flex items-center justify-between">
                      <h3 className="font-heading text-lg font-semibold text-[#2a3b5c]">
                        Blocked dates
                      </h3>
                      {loadingDates && (
                        <Loader2 className="w-4 h-4 text-[#3d6fa8] animate-spin" />
                      )}
                    </div>
                    {dates.length === 0 && !loadingDates && (
                      <div className="p-10 text-center text-sm text-[#5c6b85]">
                        No blocked dates yet. Everything is open for booking.
                      </div>
                    )}
                    <ul>
                      {dates.map((d) => (
                        <li
                          key={d.date}
                          data-testid={`${ADMIN.dateRow}-${d.date}`}
                          className="flex items-center justify-between px-6 py-4 border-b border-[#dce6f0] last:border-b-0"
                        >
                          <div>
                            <div className="font-medium text-[#2a3b5c]">
                              {format(new Date(d.date), "EEEE, d MMMM yyyy")}
                            </div>
                            {d.reason && (
                              <div className="text-sm text-[#5c6b85] mt-0.5">
                                {d.reason}
                              </div>
                            )}
                          </div>
                          <button
                            data-testid={`${ADMIN.removeButton}-${d.date}`}
                            onClick={() => handleRemoveDate(d.date)}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-full text-[#5c6b85] hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Block a specific time slot */}
                  <div className="mt-4 flex items-center gap-2 text-[#3d6fa8]">
                    <Clock className="w-4 h-4" />
                    <div className="text-xs uppercase tracking-[0.2em] font-medium">
                      Or block just a specific time
                    </div>
                  </div>

                  <form
                    onSubmit={handleAddSlot}
                    className="rounded-3xl bg-[#fff8df] border border-[#f0e3a8] p-8 grid grid-cols-1 md:grid-cols-4 gap-5"
                  >
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Date
                      </Label>
                      <Input
                        data-testid="admin-slot-date-input"
                        type="date"
                        min={todayIso}
                        value={slotDate}
                        onChange={(e) => setSlotDate(e.target.value)}
                        className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Time
                      </Label>
                      <Select value={slotTime} onValueChange={setSlotTime}>
                        <SelectTrigger
                          data-testid="admin-slot-time-trigger"
                          className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                        >
                          <SelectValue placeholder="Pick a time" />
                        </SelectTrigger>
                        <SelectContent>
                          {AVAILABLE_TIME_OPTIONS.map((t) => (
                            <SelectItem key={t} value={t} data-testid={`admin-slot-time-${t}`}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Reason (optional)
                      </Label>
                      <Input
                        data-testid="admin-slot-reason-input"
                        value={slotReason}
                        onChange={(e) => setSlotReason(e.target.value)}
                        placeholder="e.g. school staff meeting"
                        className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                      />
                    </div>
                    <div className="md:col-span-4 flex justify-end">
                      <Button
                        data-testid="admin-slot-add-button"
                        type="submit"
                        disabled={addingSlot || !slotDate || !slotTime}
                        className="rounded-full px-6 py-6 bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c]"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {addingSlot ? "Blocking..." : "Block this slot"}
                      </Button>
                    </div>
                  </form>

                  <div className="rounded-3xl bg-white border border-[#dce6f0] p-2">
                    <div className="px-6 py-4 border-b border-[#dce6f0] flex items-center justify-between">
                      <h3 className="font-heading text-lg font-semibold text-[#2a3b5c]">
                        Blocked time slots
                      </h3>
                      {loadingSlots && (
                        <Loader2 className="w-4 h-4 text-[#3d6fa8] animate-spin" />
                      )}
                    </div>
                    {slots.length === 0 && !loadingSlots && (
                      <div className="p-10 text-center text-sm text-[#5c6b85]">
                        No individual time slots blocked. Days listed above will hide every slot for that date.
                      </div>
                    )}
                    <ul>
                      {slots.map((s) => (
                        <li
                          key={s.id}
                          data-testid={`admin-slot-row-${s.id}`}
                          className="flex items-center justify-between px-6 py-4 border-b border-[#dce6f0] last:border-b-0"
                        >
                          <div>
                            <div className="font-medium text-[#2a3b5c]">
                              {(() => {
                                try {
                                  return format(new Date(s.date), "EEEE, d MMMM yyyy");
                                } catch {
                                  return s.date;
                                }
                              })()}
                              {" · "}
                              <span className="text-[#3d6fa8]">{s.time}</span>
                            </div>
                            {s.reason && (
                              <div className="text-sm text-[#5c6b85] mt-0.5">
                                {s.reason}
                              </div>
                            )}
                          </div>
                          <button
                            data-testid={`admin-slot-remove-${s.id}`}
                            onClick={() => handleRemoveSlot(s.id)}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-full text-[#5c6b85] hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

              {tab === "testimonials" && (
                <>
                  <form
                    onSubmit={handleAddTestimonial}
                    className="rounded-3xl bg-[#f0f6fc] border border-[#dce6f0] p-8 grid grid-cols-1 md:grid-cols-3 gap-5"
                  >
                    <div className="md:col-span-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Name / attribution
                      </Label>
                      <Input
                        data-testid={ADMIN.testimonialNameInput}
                        value={tName}
                        onChange={(e) => setTName(e.target.value)}
                        placeholder="e.g. Sarah · Parent of Y10 student"
                        className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Rating
                      </Label>
                      <Select value={tRating} onValueChange={setTRating}>
                        <SelectTrigger
                          data-testid={ADMIN.testimonialRatingSelect}
                          className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[5, 4, 3, 2, 1].map((n) => (
                            <SelectItem key={n} value={String(n)}>
                              {"★".repeat(n)} ({n})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Review quote
                      </Label>
                      <Textarea
                        data-testid={ADMIN.testimonialQuoteInput}
                        value={tQuote}
                        onChange={(e) => setTQuote(e.target.value)}
                        placeholder="What did they say?"
                        className="mt-3 rounded-2xl min-h-[120px] bg-white border-[#dce6f0]"
                      />
                    </div>
                    <div className="md:col-span-3 flex justify-end">
                      <Button
                        data-testid={ADMIN.testimonialAddButton}
                        type="submit"
                        disabled={addingT || !tName || !tQuote}
                        className="rounded-full px-6 py-6 bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c]"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {addingT ? "Adding..." : "Publish review"}
                      </Button>
                    </div>
                  </form>

                  <div className="rounded-3xl bg-white border border-[#dce6f0] p-2">
                    <div className="px-6 py-4 border-b border-[#dce6f0] flex items-center justify-between">
                      <h3 className="font-heading text-lg font-semibold text-[#2a3b5c]">
                        Published reviews
                      </h3>
                      {loadingT && (
                        <Loader2 className="w-4 h-4 text-[#3d6fa8] animate-spin" />
                      )}
                    </div>
                    {testimonials.length === 0 && !loadingT && (
                      <div className="p-10 text-center text-sm text-[#5c6b85]">
                        No reviews yet. Add one above to publish it on the site.
                      </div>
                    )}
                    <ul>
                      {testimonials.map((t) => (
                        <li
                          key={t.id}
                          data-testid={`${ADMIN.testimonialRow}-${t.id}`}
                          className="px-6 py-5 border-b border-[#dce6f0] last:border-b-0"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {Array.from({ length: t.rating }).map((_, i) => (
                                  <Star key={i} className="w-3.5 h-3.5 fill-[#3d6fa8] text-[#3d6fa8]" />
                                ))}
                              </div>
                              <p className="mt-2 text-[#2a3b5c] leading-relaxed">
                                &ldquo;{t.quote}&rdquo;
                              </p>
                              <div className="mt-2 text-sm text-[#5c6b85]">— {t.name}</div>
                            </div>
                            <button
                              data-testid={`${ADMIN.testimonialRemoveButton}-${t.id}`}
                              onClick={() => handleRemoveTestimonial(t.id)}
                              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-full text-[#5c6b85] hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                              Remove
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

              {tab === "resources" && (
                <>
                  <form
                    onSubmit={handleAddResource}
                    className="rounded-3xl bg-[#f0f6fc] border border-[#dce6f0] p-8 grid grid-cols-1 md:grid-cols-2 gap-5"
                  >
                    <div className="md:col-span-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Title
                      </Label>
                      <Input
                        data-testid={ADMIN_RESOURCE.titleInput}
                        value={rTitle}
                        onChange={(e) => setRTitle(e.target.value)}
                        placeholder="e.g. GCSE Chemistry — Bonding Checklist"
                        className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Description
                      </Label>
                      <Textarea
                        data-testid={ADMIN_RESOURCE.descriptionInput}
                        value={rDesc}
                        onChange={(e) => setRDesc(e.target.value)}
                        placeholder="What does the buyer get? Topics covered, pages, etc."
                        className="mt-3 rounded-2xl min-h-[100px] bg-white border-[#dce6f0]"
                      />
                    </div>

                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Subject
                      </Label>
                      <Select value={rCategory} onValueChange={setRCategory}>
                        <SelectTrigger
                          data-testid={ADMIN_RESOURCE.categorySelect}
                          className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="chemistry">Chemistry</SelectItem>
                          <SelectItem value="physics">Physics</SelectItem>
                          <SelectItem value="biology">Biology</SelectItem>
                          <SelectItem value="maths">Maths</SelectItem>
                          <SelectItem value="general">General / mixed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Level
                      </Label>
                      <Select value={rLevel} onValueChange={setRLevel}>
                        <SelectTrigger
                          data-testid={ADMIN_RESOURCE.levelSelect}
                          className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="KS3">KS3 (Y7–9)</SelectItem>
                          <SelectItem value="KS4">KS4 (Y10–11, GCSE)</SelectItem>
                          <SelectItem value="KS5">KS5 (Y12–13, A-Level)</SelectItem>
                          <SelectItem value="all">All levels</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Type
                      </Label>
                      <Select value={rType} onValueChange={setRType}>
                        <SelectTrigger
                          data-testid={ADMIN_RESOURCE.typeSelect}
                          className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="checklist">Checklist</SelectItem>
                          <SelectItem value="flashcards">Flashcards</SelectItem>
                          <SelectItem value="specification">Specification made easy</SelectItem>
                          <SelectItem value="practice">Practice + markscheme</SelectItem>
                          <SelectItem value="powerpoint">Lesson PowerPoint</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Price (£)
                      </Label>
                      <Input
                        data-testid={ADMIN_RESOURCE.priceInput}
                        type="number"
                        min="0"
                        step="0.01"
                        value={rPrice}
                        onChange={(e) => setRPrice(e.target.value)}
                        className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        File (PDF, PPTX, DOCX, ZIP — max 50 MB)
                      </Label>
                      <input
                        data-testid={ADMIN_RESOURCE.fileInput}
                        type="file"
                        accept=".pdf,.ppt,.pptx,.doc,.docx,.zip,.png,.jpg,.jpeg"
                        onChange={(e) => setRFile(e.target.files?.[0] || null)}
                        className="mt-3 block w-full text-sm text-[#5c6b85]
                          file:mr-4 file:py-3 file:px-5 file:rounded-full file:border-0
                          file:text-sm file:font-medium file:bg-[#ffe89a] file:text-[#2a3b5c]
                          hover:file:bg-[#fcd66b] cursor-pointer"
                      />
                      {rFile && (
                        <p className="mt-2 text-xs text-[#5c6b85]">
                          Selected: <span className="text-[#2a3b5c]">{rFile.name}</span> ({(rFile.size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                      )}
                    </div>

                    <div className="md:col-span-2 flex justify-end">
                      <Button
                        data-testid={ADMIN_RESOURCE.submitButton}
                        type="submit"
                        disabled={uploading || !rTitle || !rFile}
                        className="rounded-full px-6 py-6 bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c]"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Publish resource
                          </>
                        )}
                      </Button>
                    </div>
                  </form>

                  <div className="rounded-3xl bg-white border border-[#dce6f0] p-2">
                    <div className="px-6 py-4 border-b border-[#dce6f0] flex items-center justify-between">
                      <h3 className="font-heading text-lg font-semibold text-[#2a3b5c]">
                        Published resources
                      </h3>
                      {loadingR && (
                        <Loader2 className="w-4 h-4 text-[#3d6fa8] animate-spin" />
                      )}
                    </div>
                    {resources.length === 0 && !loadingR && (
                      <div className="p-10 text-center text-sm text-[#5c6b85]">
                        No resources yet. Upload your first one above.
                      </div>
                    )}
                    <ul>
                      {resources.map((r) => (
                        <li
                          key={r.id}
                          data-testid={`${ADMIN_RESOURCE.row}-${r.id}`}
                          className="flex items-start justify-between gap-4 px-6 py-5 border-b border-[#dce6f0] last:border-b-0"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs uppercase tracking-[0.15em] font-medium px-2 py-0.5 rounded-full bg-[#f0f6fc] text-[#3d6fa8]">
                                {r.resource_type}
                              </span>
                              <span className="text-xs uppercase tracking-[0.15em] font-medium px-2 py-0.5 rounded-full bg-[#ffe89a]/50 text-[#2a3b5c]">
                                {r.category}
                              </span>
                              <span className="text-xs uppercase tracking-[0.15em] font-medium px-2 py-0.5 rounded-full bg-[#f0f6fc] text-[#5c6b85]">
                                {r.level}
                              </span>
                              {!r.active && (
                                <span className="text-xs uppercase tracking-[0.15em] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-600">
                                  hidden
                                </span>
                              )}
                            </div>
                            <div className="mt-2 font-medium text-[#2a3b5c] truncate">
                              {r.title}
                            </div>
                            <div className="text-sm text-[#5c6b85] mt-0.5">
                              £{Number(r.price).toFixed(2)} · {r.file_name}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              data-testid={`${ADMIN_RESOURCE.toggleButton}-${r.id}`}
                              onClick={() => handleToggleResource(r.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-full text-[#5c6b85] hover:text-[#3d6fa8] hover:bg-[#f0f6fc] transition-colors"
                              title={r.active ? "Hide from site" : "Show on site"}
                            >
                              {r.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                              {r.active ? "Hide" : "Show"}
                            </button>
                            <button
                              data-testid={`${ADMIN_RESOURCE.deleteButton}-${r.id}`}
                              onClick={() => handleDeleteResource(r.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-full text-[#5c6b85] hover:text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

              {tab === "messages" && (
                <div className="rounded-3xl bg-white border border-[#dce6f0] p-2">
                  <div className="px-6 py-4 border-b border-[#dce6f0] flex items-center justify-between">
                    <h3 className="font-heading text-lg font-semibold text-[#2a3b5c]">
                      Contact form submissions
                    </h3>
                    {loadingM && (
                      <Loader2 className="w-4 h-4 text-[#3d6fa8] animate-spin" />
                    )}
                  </div>
                  {messages.length === 0 && !loadingM && (
                    <div className="p-10 text-center text-sm text-[#5c6b85]">
                      No messages yet. Anything submitted through the website
                      contact form (including bundle enquiries) will appear here.
                    </div>
                  )}
                  <ul>
                    {messages.map((m) => (
                      <li
                        key={m.id}
                        data-testid={`admin-message-row-${m.id}`}
                        className="px-6 py-5 border-b border-[#dce6f0] last:border-b-0"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                              <span className="font-medium text-[#2a3b5c]">
                                {m.name}
                              </span>
                              <a
                                href={`mailto:${m.email}?subject=Re: your message to Kenisha's Tutoring`}
                                className="inline-flex items-center gap-1.5 text-sm text-[#3d6fa8] hover:underline"
                              >
                                <Mail className="w-3.5 h-3.5" />
                                {m.email}
                              </a>
                              <span className="text-xs text-[#5c6b85]">
                                {(() => {
                                  try {
                                    return format(new Date(m.created_at), "d MMM yyyy · HH:mm");
                                  } catch {
                                    return m.created_at;
                                  }
                                })()}
                              </span>
                            </div>
                            <p className="mt-3 text-[#2a3b5c] leading-relaxed whitespace-pre-wrap break-words">
                              {m.message}
                            </p>
                          </div>
                          <button
                            data-testid={`admin-message-delete-${m.id}`}
                            onClick={() => handleDeleteMessage(m.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-full text-[#5c6b85] hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {tab === "invoicing" && (
                <div className="space-y-6">
                  <div className="rounded-3xl bg-[#fff8df] border border-[#f3e4a0] p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-white border border-[#f3e4a0] flex items-center justify-center text-[#a48414] shrink-0">
                      <Wand2 className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-[#2a3b5c] text-base font-medium">
                        Generate a branded invoice or receipt
                      </h3>
                      <p className="text-sm text-[#5c6b85] mt-1">
                        Auto-populate from a booking, then tweak any field before downloading the PDF. Bank details are remembered on this device.
                      </p>
                    </div>
                  </div>

                  <div className="rounded-3xl bg-[#f0f6fc] border border-[#dce6f0] p-8 space-y-6">
                    {/* Doc type + booking selector */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                          Document type
                        </Label>
                        <div className="mt-3 inline-flex w-full rounded-2xl bg-white border border-[#dce6f0] p-1">
                          <button
                            type="button"
                            data-testid="invoicing-doctype-invoice"
                            onClick={() => setInvDocType("invoice")}
                            className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm transition-colors ${
                              invDocType === "invoice"
                                ? "bg-[#8ab8e8] text-[#2a3b5c]"
                                : "text-[#5c6b85] hover:text-[#3d6fa8]"
                            }`}
                          >
                            <FileText className="w-4 h-4" /> Invoice
                          </button>
                          <button
                            type="button"
                            data-testid="invoicing-doctype-receipt"
                            onClick={() => setInvDocType("receipt")}
                            className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm transition-colors ${
                              invDocType === "receipt"
                                ? "bg-[#ffe89a] text-[#2a3b5c]"
                                : "text-[#5c6b85] hover:text-[#3d6fa8]"
                            }`}
                          >
                            <FileCheck2 className="w-4 h-4" /> Receipt
                          </button>
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                          Auto-fill from booking (optional)
                        </Label>
                        <Select
                          value={invSelectedBookingId || "none"}
                          onValueChange={(v) => loadFromBooking(v === "none" ? "" : v)}
                        >
                          <SelectTrigger
                            data-testid="invoicing-booking-select"
                            className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                          >
                            <SelectValue placeholder="Choose a booking…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No booking · start blank</SelectItem>
                            {bookings.slice(0, 60).map((b) => {
                              let label = b.id;
                              try {
                                label = `${b.student_name || "Client"} · ${format(parseISO(b.session_date || b.date), "d MMM")} ${b.session_time || b.time || ""}`;
                              } catch {}
                              return (
                                <SelectItem key={b.id} value={b.id}>
                                  {label}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Document number + dates */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                          {invIsReceipt ? "Receipt number" : "Invoice number"} <span className="text-[#5c6b85] normal-case tracking-normal">(auto if blank)</span>
                        </Label>
                        <Input
                          data-testid="invoicing-doc-number"
                          value={invNumber}
                          onChange={(e) => setInvNumber(e.target.value)}
                          placeholder={invIsReceipt ? "REC-2026-001" : "INV-2026-001"}
                          className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                        />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                          Issue date
                        </Label>
                        <Input
                          type="date"
                          data-testid="invoicing-issue-date"
                          value={invIssueDate}
                          onChange={(e) => setInvIssueDate(e.target.value)}
                          className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                        />
                      </div>
                      {invIsReceipt ? (
                        <div>
                          <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                            Paid date
                          </Label>
                          <Input
                            type="date"
                            data-testid="invoicing-paid-date"
                            value={invPaidDate}
                            onChange={(e) => setInvPaidDate(e.target.value)}
                            className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                          />
                        </div>
                      ) : (
                        <div>
                          <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                            Due date
                          </Label>
                          <Input
                            type="date"
                            data-testid="invoicing-due-date"
                            value={invDueDate}
                            onChange={(e) => setInvDueDate(e.target.value)}
                            className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                          />
                        </div>
                      )}
                    </div>

                    {invIsReceipt && (
                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                          Payment method
                        </Label>
                        <Input
                          data-testid="invoicing-payment-method"
                          value={invPaymentMethod}
                          onChange={(e) => setInvPaymentMethod(e.target.value)}
                          placeholder="Bank transfer / Cash / Card (Stripe)"
                          className="mt-3 rounded-2xl h-12 bg-white border-[#dce6f0]"
                        />
                      </div>
                    )}

                    {/* Bill to */}
                    <div className="pt-2 border-t border-[#dce6f0]">
                      <div className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium mb-4">
                        Bill to
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <Label className="text-xs text-[#5c6b85]">Name</Label>
                          <Input
                            data-testid="invoicing-bill-name"
                            value={invBillName}
                            onChange={(e) => setInvBillName(e.target.value)}
                            placeholder="Parent or student name"
                            className="mt-2 rounded-2xl h-12 bg-white border-[#dce6f0]"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-[#5c6b85]">Email</Label>
                          <Input
                            type="email"
                            data-testid="invoicing-bill-email"
                            value={invBillEmail}
                            onChange={(e) => setInvBillEmail(e.target.value)}
                            placeholder="name@example.com"
                            className="mt-2 rounded-2xl h-12 bg-white border-[#dce6f0]"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs text-[#5c6b85]">Address (optional)</Label>
                          <Textarea
                            data-testid="invoicing-bill-address"
                            value={invBillAddress}
                            onChange={(e) => setInvBillAddress(e.target.value)}
                            placeholder="Street, City, Postcode"
                            className="mt-2 rounded-2xl min-h-[80px] bg-white border-[#dce6f0]"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Line items */}
                    <div className="pt-2 border-t border-[#dce6f0]">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                          Line items
                        </div>
                        <button
                          type="button"
                          data-testid="invoicing-add-item"
                          onClick={handleAddInvItem}
                          className="inline-flex items-center gap-1.5 text-sm text-[#3d6fa8] hover:text-[#2a3b5c]"
                        >
                          <Plus className="w-4 h-4" /> Add line
                        </button>
                      </div>
                      <div className="space-y-3">
                        {invItems.map((it, idx) => (
                          <div
                            key={idx}
                            className="grid grid-cols-12 gap-3 items-start bg-white rounded-2xl border border-[#dce6f0] p-3"
                          >
                            <div className="col-span-12 md:col-span-7">
                              <Label className="text-[10px] uppercase tracking-[0.15em] text-[#5c6b85]">Description</Label>
                              <Input
                                data-testid={`invoicing-item-desc-${idx}`}
                                value={it.description}
                                onChange={(e) => handleInvItemChange(idx, "description", e.target.value)}
                                placeholder="e.g. KS5 Chemistry · 60-min session · Sat 8 Mar 10:00"
                                className="mt-1 rounded-xl h-11 bg-white border-[#dce6f0]"
                              />
                            </div>
                            <div className="col-span-4 md:col-span-2">
                              <Label className="text-[10px] uppercase tracking-[0.15em] text-[#5c6b85]">Qty</Label>
                              <Input
                                type="number"
                                min="1"
                                step="1"
                                data-testid={`invoicing-item-qty-${idx}`}
                                value={it.quantity}
                                onChange={(e) => handleInvItemChange(idx, "quantity", e.target.value)}
                                className="mt-1 rounded-xl h-11 bg-white border-[#dce6f0]"
                              />
                            </div>
                            <div className="col-span-6 md:col-span-2">
                              <Label className="text-[10px] uppercase tracking-[0.15em] text-[#5c6b85]">Unit £</Label>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                data-testid={`invoicing-item-price-${idx}`}
                                value={it.unit_price}
                                onChange={(e) => handleInvItemChange(idx, "unit_price", e.target.value)}
                                className="mt-1 rounded-xl h-11 bg-white border-[#dce6f0]"
                              />
                            </div>
                            <div className="col-span-2 md:col-span-1 flex md:justify-end pt-5">
                              <button
                                type="button"
                                data-testid={`invoicing-item-remove-${idx}`}
                                onClick={() => handleRemoveInvItem(idx)}
                                disabled={invItems.length <= 1}
                                className="inline-flex items-center justify-center w-9 h-9 rounded-full text-[#5c6b85] hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#5c6b85] transition-colors"
                                aria-label="Remove line"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 flex items-center justify-end gap-3 text-sm">
                        <span className="text-[#5c6b85]">Total</span>
                        <span
                          data-testid="invoicing-total"
                          className="text-[#2a3b5c] text-lg font-medium"
                        >
                          £{invSubtotal.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {/* Bank details (invoices only) */}
                    {!invIsReceipt && (
                      <div className="pt-2 border-t border-[#dce6f0]">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium mb-4">
                          Bank details (for bank transfer)
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          <div>
                            <Label className="text-xs text-[#5c6b85]">Account name</Label>
                            <Input
                              data-testid="invoicing-bank-name"
                              value={invAccountName}
                              onChange={(e) => setInvAccountName(e.target.value)}
                              placeholder="Kenisha's Tutoring"
                              className="mt-2 rounded-2xl h-12 bg-white border-[#dce6f0]"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[#5c6b85]">Reference</Label>
                            <Input
                              data-testid="invoicing-bank-reference"
                              value={invReference}
                              onChange={(e) => setInvReference(e.target.value)}
                              placeholder="Surname / booking ref"
                              className="mt-2 rounded-2xl h-12 bg-white border-[#dce6f0]"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[#5c6b85]">Sort code</Label>
                            <Input
                              data-testid="invoicing-bank-sort"
                              value={invSortCode}
                              onChange={(e) => setInvSortCode(e.target.value)}
                              placeholder="00-00-00"
                              className="mt-2 rounded-2xl h-12 bg-white border-[#dce6f0]"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[#5c6b85]">Account number</Label>
                            <Input
                              data-testid="invoicing-bank-account"
                              value={invAccountNumber}
                              onChange={(e) => setInvAccountNumber(e.target.value)}
                              placeholder="12345678"
                              className="mt-2 rounded-2xl h-12 bg-white border-[#dce6f0]"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    <div className="pt-2 border-t border-[#dce6f0]">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                        Notes (optional)
                      </Label>
                      <Textarea
                        data-testid="invoicing-notes"
                        value={invNotes}
                        onChange={(e) => setInvNotes(e.target.value)}
                        placeholder={invIsReceipt
                          ? "Thank you for your payment!"
                          : "Please pay within 14 days. Thank you!"}
                        className="mt-3 rounded-2xl min-h-[80px] bg-white border-[#dce6f0]"
                      />
                    </div>

                    {/* Action */}
                    <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
                      <button
                        type="button"
                        data-testid="invoicing-generate-pdf"
                        onClick={handleGenerateInvoicePdf}
                        disabled={generatingPdf}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2a3b5c] hover:bg-[#1f2c46] text-white px-6 py-3 text-sm transition-colors disabled:opacity-60"
                      >
                        {generatingPdf ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" /> Generating…
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            Download {invIsReceipt ? "receipt" : "invoice"} PDF
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
